import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2 } from 'lucide-react';
import { useChatStore, type ChatMessage } from '../stores/chatStore';
import { useUIStore } from '../stores/uiStore';
import { assistantApi } from '../services/api';

// Quick actions per role
const QUICK_ACTIONS: Record<string, { en: string; es: string; message: string }[]> = {
  operator: [
    { en: 'Fleet Status', es: 'Estado Flota', message: 'What is the fleet status?' },
    { en: 'Critical Alerts', es: 'Alertas Críticas', message: 'Show me critical alerts' },
    { en: 'Help', es: 'Ayuda', message: 'What can you do? Help me' },
  ],
  engineer: [
    { en: 'Predict RUL', es: 'Predecir RUL', message: 'Predict the remaining useful life' },
    { en: 'Run RCA', es: 'Ejecutar RCA', message: 'Run root cause analysis' },
    { en: 'Physics Models', es: 'Modelos Físicos', message: 'Explain the physics models' },
  ],
  auditor: [
    { en: 'Traceability', es: 'Trazabilidad', message: 'Show traceability records' },
    { en: 'Compliance', es: 'Cumplimiento', message: 'Generate compliance report' },
    { en: 'Model History', es: 'Historial Modelo', message: 'Compare the ML models' },
  ],
};

export function ChatWidget() {
  const { messages, isOpen, isLoading, toggleChat, addMessage, setLoading, clearMessages } = useChatStore();
  const { viewMode, language } = useUIStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text.trim(),
      timestamp: new Date().toISOString(),
    };
    addMessage(userMsg);
    setInput('');
    setLoading(true);

    try {
      // Build conversation history for multi-turn context
      const history = messages.map((m) => ({
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));

      const response = await assistantApi.chat({
        message: text.trim(),
        language,
        role: viewMode,
        context: {},
        history,
      });

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: response.reply,
        intent: response.intent,
        confidence: response.confidence,
        data: response.data,
        actions: response.actions,
        sources: response.sources,
        timestamp: response.timestamp,
      };
      addMessage(assistantMsg);
    } catch {
      addMessage({
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: language === 'es'
          ? 'Error al conectar con el asistente. Verifica que el backend esté activo.'
          : 'Error connecting to assistant. Please check that the backend is running.',
        timestamp: new Date().toISOString(),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const quickActions = QUICK_ACTIONS[viewMode] || QUICK_ACTIONS.engineer;

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary-600 hover:bg-primary-500
                   text-white shadow-lg shadow-primary-600/30 flex items-center justify-center
                   transition-all duration-200 hover:scale-110"
        title={language === 'es' ? 'Abrir Asistente' : 'Open Assistant'}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-[28rem] bg-dark-900 border border-dark-700
                    rounded-2xl shadow-2xl shadow-black/40 flex flex-col overflow-hidden"
         style={{ height: 'calc(100vh - 6rem)', maxHeight: '48rem' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-dark-800 border-b border-dark-700">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-600/20 flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-primary-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white">InverterAI Assistant</h3>
            <span className="text-xs text-dark-400 capitalize">{viewMode} mode</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearMessages}
            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-200 transition-colors"
            title={language === 'es' ? 'Limpiar chat' : 'Clear chat'}
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={toggleChat}
            className="p-1.5 rounded-lg hover:bg-dark-700 text-dark-400 hover:text-dark-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <MessageCircle className="w-10 h-10 text-dark-600 mx-auto mb-3" />
            <p className="text-sm text-dark-400 mb-4">
              {language === 'es'
                ? `Hola! Soy el asistente InverterAI. Como ${viewMode}, puedo ayudarte con:`
                : `Hi! I'm the InverterAI assistant. As ${viewMode}, I can help you with:`}
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {quickActions.map((action, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(action.message)}
                  className="px-3 py-1.5 text-xs rounded-full bg-dark-800 border border-dark-600
                             text-dark-300 hover:text-primary-400 hover:border-primary-600/50 transition-colors"
                >
                  {language === 'es' ? action.es : action.en}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'bg-primary-600/20 text-primary-100 rounded-br-sm'
                  : 'bg-dark-800 text-dark-200 rounded-bl-sm'
              }`}
            >
              <p className="whitespace-pre-wrap break-words">{msg.content}</p>

              {/* Actions */}
              {msg.actions && msg.actions.length > 0 && (
                <div className="mt-2 pt-2 border-t border-dark-700/50">
                  <p className="text-xs text-dark-400 mb-1">
                    {language === 'es' ? 'Acciones:' : 'Actions:'}
                  </p>
                  {msg.actions.map((action, i) => (
                    <p key={i} className="text-xs text-solar-400">• {action}</p>
                  ))}
                </div>
              )}

              {/* Sources */}
              {msg.sources && msg.sources.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {msg.sources.slice(0, 3).map((src, i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-dark-700 text-dark-400">
                      {src}
                    </span>
                  ))}
                </div>
              )}

              {/* Intent badge (only for assistant messages) */}
              {msg.role === 'assistant' && msg.intent && (
                <div className="mt-1 flex items-center gap-1.5">
                  <span className="text-[10px] text-dark-500">
                    {msg.intent} ({((msg.confidence || 0) * 100).toFixed(0)}%)
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-dark-800 rounded-xl px-4 py-2 rounded-bl-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-primary-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions bar (shown when messages exist) */}
      {messages.length > 0 && (
        <div className="px-3 py-1.5 border-t border-dark-800 flex gap-1.5 overflow-x-auto scrollbar-hide">
          {quickActions.map((action, i) => (
            <button
              key={i}
              onClick={() => sendMessage(action.message)}
              className="flex-shrink-0 px-2 py-1 text-[10px] rounded-full bg-dark-800 border border-dark-700
                         text-dark-400 hover:text-primary-400 hover:border-primary-600/50 transition-colors"
            >
              {language === 'es' ? action.es : action.en}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-dark-700 bg-dark-850">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={language === 'es' ? 'Pregunta al asistente...' : 'Ask the assistant...'}
            disabled={isLoading}
            className="flex-1 bg-dark-800 border border-dark-600 rounded-lg px-3 py-2 text-sm text-white
                       placeholder-dark-500 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30
                       disabled:opacity-50"
          />
          <button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isLoading}
            className="px-3 py-2 bg-primary-600 hover:bg-primary-500 disabled:bg-dark-700 disabled:text-dark-500
                       text-white rounded-lg transition-colors"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
